/**
 * ServerLogsTab — Cloud-only server log table with search, sort, and pagination.
 *
 * Columns: Timestamp (UTC → local converted), Log Event
 */

import { useState, useMemo, useCallback } from 'react';
import { convertIsoToLocal } from '../../../../libs.jsx';

const ServerLogsTab = ({ logData }) => {
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('create_time');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filtered = useMemo(() => {
    let rows = [...(logData || [])];
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.log_event || '').toLowerCase().includes(q) ||
        (r.create_time || '').toLowerCase().includes(q)
      );
    }
    rows.sort((a, b) => {
      const aVal = (a[sortCol] || '').toString().toLowerCase();
      const bVal = (b[sortCol] || '').toString().toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [logData, search, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = useCallback((col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'create_time' ? 'desc' : 'asc');
    }
    setPage(1);
  }, [sortCol]);

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
    return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">Server Logs ({filtered.length})</h3>
      </div>

      <div className="pp-section__body">
        {/* Search */}
        <div className="dp-controls" style={{ marginBottom: 'var(--space-3)' }}>
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
              <input
                className="dp-search__input"
                placeholder="Search logs…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="dp-empty" style={{ padding: 'var(--space-8) var(--space-4)' }}>
            <p className="dp-empty__title">No server logs</p>
            <p className="dp-empty__text">Server logs are not available for this server.</p>
          </div>
        ) : (
          <>
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th" style={{ width: '200px' }}>
                      <button className="dp-th__sort-btn" onClick={() => handleSort('create_time')}>
                        Timestamp <SortIcon col="create_time" />
                      </button>
                    </th>
                    <th className="dp-th">
                      <button className="dp-th__sort-btn" onClick={() => handleSort('log_event')}>
                        Log Event <SortIcon col="log_event" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, idx) => (
                    <tr key={idx} className="dp-tr">
                      <td className="dp-td">
                        <span className="dp-cell-text dp-cell-text--nowrap">
                          {row.create_time ? convertIsoToLocal(row.create_time) : '—'}
                        </span>
                      </td>
                      <td className="dp-td">
                        <span className="dp-cell-text">{row.log_event || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > perPage && (
              <div className="dp-pagination">
                <span className="dp-pagination__info">
                  Showing <span className="dp-pagination__emphasis">{(page - 1) * perPage + 1}</span>
                  –<span className="dp-pagination__emphasis">{Math.min(page * perPage, filtered.length)}</span>
                  {' '}of <span className="dp-pagination__emphasis">{filtered.length}</span>
                </span>
                <div className="dp-pagination__controls">
                  <div className="dp-per-page">
                    <span className="dp-per-page__label">Per page:</span>
                    <select className="dp-per-page__select" value={perPage} onChange={(e) => { setPerPage(+e.target.value); setPage(1); }}>
                      {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="dp-page-nav">
                    <button className="dp-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                      Math.max(0, page - 3), Math.min(totalPages, page + 2)
                    ).map((p) => (
                      <button
                        key={p}
                        className={`dp-page-num${p === page ? ' is-active' : ''}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button className="dp-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ServerLogsTab;
